import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RoseLogo, RoseMark } from '@inboundx/shared/ui';
import {
    BarChart3,
    BookOpen,
    Bot,
    Building2,
    ChevronRight,
    Eye,
    EyeOff,
    Fingerprint,
    FlaskConical,
    Home,
    LifeBuoy,
    Megaphone,
    MessageCircle,
    MessageSquare,
    Pin,
    Route,
    Settings,
    Shield,
    Sparkles,
    SquarePen,
    Tags,
    Users,
    X,
} from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SidebarSetupFooter } from '@/components/setup/SidebarSetupFooter';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarRail,
    Sidebar as SidebarUI,
    useSidebar,
} from '@/components/ui/sidebar';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { usePinnedPages } from '@/contexts/PinnedPagesContext';
import { useKnowledgeLearning } from '@/hooks/useKnowledgeLearning';
import type { PinnedPage } from '@/services/pinnedPages';

export interface NavItem {
    title: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    disabled?: boolean;
    live?: boolean;
    staffOnly?: boolean;
}

interface NavSubgroup {
    title?: string;
    items: NavItem[];
}

interface NavSection {
    title: string;
    items?: NavItem[];
    subgroups?: NavSubgroup[];
}

const baseNavigation: NavSection[] = [
    {
        title: '',
        items: [{ title: 'Home', href: '/', icon: Home }],
    },
    {
        title: 'ANALYZE',
        items: [
            { title: 'Top Topics', href: '/analytics/topics', icon: MessageCircle },
            { title: 'Insights', href: '/analytics/insights', icon: Sparkles, staffOnly: true },
            { title: 'Analytics', href: '/analytics', icon: BarChart3 },
        ],
    },
    {
        title: 'MONITOR',
        items: [
            { title: 'Conversations', href: '/conversations', icon: MessageSquare },
            { title: 'Accounts', href: '/accounts', icon: Building2 },
            { title: 'Visitors', href: '/visitors', icon: Users },
        ],
    },
    {
        title: 'BUILD',
        items: [{ title: 'Config Studio', href: '/config-studio', icon: Bot }],
    },
];

// Host-staff-only section. Collapsible and folded by default — distinct from a
// future per-tenant "Admin" role. Renders as a single foldable nav item.
const superAdminItems: NavItem[] = [
    { title: 'Client stats', href: '/superadmin/clients/stats', icon: BarChart3 },
    { title: 'Edit clients', href: '/superadmin/clients/edit', icon: SquarePen },
    { title: 'Workspaces', href: '/superadmin/sites', icon: Route },
    { title: 'Users', href: '/superadmin/users', icon: Shield },
    { title: 'Experiments', href: '/superadmin/experiments', icon: FlaskConical },
    { title: 'Annotations', href: '/superadmin/annotations', icon: Tags },
    { title: 'Identity', href: '/superadmin/person-identity', icon: Fingerprint },
    { title: 'Notices', href: '/superadmin/notices', icon: Megaphone },
    { title: 'AI agents', href: '/superadmin/webmcp', icon: Bot },
];

const bottomItems: NavItem[] = [
    { title: 'Documentation', href: '/documentation', icon: BookOpen },
    { title: 'Support', href: '/support', icon: LifeBuoy },
    { title: 'Settings', href: '/settings', icon: Settings },
];

const BACKOFFICE_APP_VERSION =
    typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__ ? __APP_VERSION__ : '0.0.0-dummy';

const routeImports: Record<string, () => Promise<unknown>> = {
    '/': () => import('@/pages/HomePage'),
    '/analytics': () => import('@/pages/AnalyticsPage'),
    '/analytics/insights': () => import('@/pages/InsightsPage'),
    '/accounts': () => import('@/pages/AccountsPage'),
    '/visitors': () => import('@/pages/VisitorsPage'),
    '/conversations': () => import('@/pages/ConversationsPage'),
    '/config-studio/data-sources': () => import('@/pages/KnowledgeSection'),
    '/config-studio/sales-guidelines': () => import('@/pages/GuidelinesSection'),
    '/config-studio/website-agent': () => import('@/pages/WebsiteAgentPage'),
    '/config-studio/content-agent': () => import('@/pages/GeoFaqsPage'),
    '/config-studio/content-agent/setup': () => import('@/pages/ContentAgentPage'),
    '/config-studio/:agentId': () => import('@/pages/AgentPlaceholderPage'),
    '/config-studio/content-agent/observatory': () => import('@/pages/GeoQuestionsPage'),
    '/config-studio/content-agent/domain': () => import('@/pages/GeoDomainPage'),
    '/config-studio/content-agent/settings': () => import('@/pages/ContentAgentSettingsPage'),
    '/documentation': () => import('@/pages/DocumentationPage'),
    '/config-studio/integration': () => import('@/pages/integrations/IntegrationsPage'),
    '/settings': () => import('@/pages/SettingsPage'),
    '/superadmin/clients/stats': () => import('@/pages/ClientsPage'),
    '/superadmin/clients/edit': () => import('@/pages/ClientEditorPage'),
    '/superadmin/sites': () => import('@/pages/AdminSitesPage'),
    '/superadmin/users': () => import('@/pages/UsersPage'),
    '/superadmin/experiments': () => import('@/pages/StaffExperimentsPage'),
    '/superadmin/experiments/new': () => import('@/pages/StaffExperimentNewPage'),
    '/superadmin/annotations': () => import('@/pages/StaffAnnotationQueuePage'),
    '/superadmin/person-identity': () => import('@/pages/PersonIdentityDiagnosticsPage'),
    '/superadmin/notices': () => import('@/pages/StaffNoticesPage'),
    '/superadmin/webmcp': () => import('@/pages/WebmcpUsagePage'),
    '/support': () => import('@/pages/SupportPage'),
    '/config-studio': () => import('@/pages/WebsiteAgentPage'),
    '/config-studio/website-agent/playground': () => import('@/pages/PlaygroundPage'),
    '/config-studio/website-agent/evaluations': () => import('@/pages/PlaygroundDatasetsPage'),
};

export function prefetchRoute(href: string) {
    const routeImport =
        routeImports[href] ??
        (/^\/config-studio\/[^/]+$/.test(href)
            ? routeImports['/config-studio/:agentId']
            : undefined);
    // Hover prefetch is best-effort: after a deploy purges old chunks it rejects, and the
    // click's lazyWithRetry handles recovery (ROSE-CLIENT-BACKOFFICE-4).
    routeImport?.().catch(() => {});
}

/** Returns the single best-matching nav href for a path (longest prefix wins),
 *  so a parent like /analytics doesn't also highlight when on /analytics/topics. */
function activeHrefFor(pathname: string, hrefs: string[]): string | null {
    let best: string | null = null;
    for (const href of hrefs) {
        const matches = pathname === href || pathname.startsWith(`${href}/`);
        if (matches && (best === null || href.length > best.length)) {
            best = href;
        }
    }
    return best;
}

export function Sidebar() {
    const location = useLocation();
    const { isStaff, isRealStaff, viewAsNonAdmin, setViewAsNonAdmin } = useBackofficeAuth();
    const { learning } = useKnowledgeLearning();

    const navigation = baseNavigation.map((section) => ({
        ...section,
        items: section.items?.filter((item) => !item.staffOnly || isStaff),
        subgroups: section.subgroups?.map((subgroup) => ({
            ...subgroup,
            items: subgroup.items.filter((item) => !item.staffOnly || isStaff),
        })),
    }));

    const allHrefs = [
        ...navigation.flatMap((s) => [
            ...(s.items ?? []),
            ...(s.subgroups?.flatMap((sub) => sub.items) ?? []),
        ]),
        ...(isStaff ? superAdminItems : []),
        ...bottomItems,
    ].map((i) => i.href);
    const activeHref = activeHrefFor(location.pathname, allHrefs);
    // Mirrors the `group-data-[collapsible=icon]` CSS: the icon rail is desktop-only,
    // the mobile sheet always shows the full sidebar.
    const { state, isMobile } = useSidebar();
    const iconOnly = !isMobile && state === 'collapsed';

    return (
        <SidebarUI collapsible="icon" className="border-r border-border bg-white">
            {/* Logo */}
            <SidebarHeader className="p-0">
                <div className="flex h-16 items-center border-b border-border-subtle px-4 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
                    <Link to="/" className="flex items-center">
                        {/* Branch on state rather than CSS: RoseLogo is a masked <span>
                            with an inline `display`, which a `hidden` class can't override. */}
                        {iconOnly ? (
                            <RoseIconMark />
                        ) : (
                            <RoseLogo width={87} height={26} color="#000000" />
                        )}
                    </Link>
                </div>
            </SidebarHeader>

            {/* Navigation */}
            <SidebarContent className="py-4">
                {navigation.map((section, index) => {
                    const renderItems = (items: NavItem[]) => (
                        <SidebarMenu className="gap-px">
                            {items.map((item) => (
                                <NavMenuItem
                                    key={item.href}
                                    item={item}
                                    isActive={item.href === activeHref}
                                    badge={
                                        learning && item.href === '/config-studio' ? (
                                            <LearningMark />
                                        ) : null
                                    }
                                />
                            ))}
                        </SidebarMenu>
                    );

                    const group = (
                        <SidebarGroup
                            key={section.title || `section-${index}`}
                            className="px-2 py-2"
                        >
                            {section.title && (
                                <SidebarGroupLabel className="mb-1.5 px-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-foreground-subtle">
                                    {section.title}
                                </SidebarGroupLabel>
                            )}
                            <SidebarGroupContent>
                                {section.subgroups
                                    ? section.subgroups.map((sub, subIndex) => (
                                          <div
                                              key={sub.title || `sub-${subIndex}`}
                                              className={subIndex > 0 ? 'mt-2' : undefined}
                                          >
                                              {sub.title && (
                                                  <div className="mb-1 flex items-center gap-2 px-2 text-[11px] font-medium tracking-[0.02em] text-muted-foreground group-data-[collapsible=icon]:hidden">
                                                      <span>{sub.title}</span>
                                                      <span
                                                          aria-hidden="true"
                                                          className="h-px flex-1 bg-border-subtle"
                                                      />
                                                  </div>
                                              )}
                                              {renderItems(sub.items)}
                                          </div>
                                      ))
                                    : section.items && renderItems(section.items)}
                            </SidebarGroupContent>
                        </SidebarGroup>
                    );

                    // Pinned section sits right under Home (the first section).
                    if (index === 0) {
                        return (
                            <Fragment key="home-and-pinned">
                                {group}
                                <PinnedSection />
                            </Fragment>
                        );
                    }
                    return group;
                })}
                {/* Day-1 setup progress — pinned above the footer divider; self-hides once complete */}
                <SidebarSetupFooter />
            </SidebarContent>

            {/* Bottom items */}
            <SidebarFooter className="border-t border-border-subtle px-2">
                <SidebarMenu className="gap-px">
                    {isRealStaff && (
                        <SuperAdminNav
                            activeHref={activeHref}
                            viewAsNonAdmin={viewAsNonAdmin}
                            onToggleViewAs={() => setViewAsNonAdmin(!viewAsNonAdmin)}
                        />
                    )}
                    {bottomItems.map((item) => (
                        <NavMenuItem
                            key={item.href}
                            item={item}
                            isActive={item.href === activeHref}
                        />
                    ))}
                </SidebarMenu>

                {/* Version */}
                <div className="pt-1 text-center group-data-[collapsible=icon]:hidden">
                    <span className="text-[10px] text-foreground-subtle">
                        v{BACKOFFICE_APP_VERSION}
                    </span>
                </div>
            </SidebarFooter>

            <SidebarRail />
        </SidebarUI>
    );
}

/** Rose spinning while it refreshes what it knows — the only progress
 *  indicator outside the Knowledge hub's own banner (IX-4673). */
export function LearningMark() {
    return <RoseMark className="size-3.5 animate-[spin_3s_linear_infinite] text-link" />;
}

function NavLiveDot() {
    return (
        <SidebarMenuBadge className="pointer-events-none mr-1">
            <span
                aria-hidden="true"
                className="block h-[7px] w-[7px] rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.18)]"
            />
        </SidebarMenuBadge>
    );
}

function NavSoonPill() {
    return (
        <SidebarMenuBadge className="pointer-events-none text-[9px] font-semibold uppercase tracking-[0.08em] text-foreground-subtle">
            Soon
        </SidebarMenuBadge>
    );
}

/** Host-staff-only "Super Admin" group — a single foldable nav item whose
 *  children are the admin pages plus a "view as non-admin" preview toggle.
 *  Opens by default when an admin page is active or the preview is on. The
 *  group itself stays visible in preview mode (gated on real staff, not the
 *  effective flag) so the toggle can always be switched back off. */
function SuperAdminNav({
    activeHref,
    viewAsNonAdmin,
    onToggleViewAs,
}: {
    activeHref: string | null;
    viewAsNonAdmin: boolean;
    onToggleViewAs: () => void;
}) {
    const hasActive = superAdminItems.some((item) => item.href === activeHref);
    return (
        <Collapsible defaultOpen={hasActive || viewAsNonAdmin} className="group/super-admin">
            <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                        tooltip="Super Admin"
                        size="lg"
                        className="h-9 py-0 group-data-[collapsible=icon]:justify-center"
                    >
                        <Shield />
                        <span className="group-data-[collapsible=icon]:hidden">Super Admin</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/super-admin:rotate-90 group-data-[collapsible=icon]:hidden" />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <SidebarMenuSub>
                        {/* Preview toggle stays visible so a super-admin can exit
                            "view as non-admin" mode. Admin page links below hide
                            while previewing. */}
                        <SidebarMenuSubItem>
                            <SidebarMenuSubButton asChild isActive={viewAsNonAdmin}>
                                <button type="button" onClick={onToggleViewAs} className="w-full">
                                    {viewAsNonAdmin ? <Eye /> : <EyeOff />}
                                    <span>
                                        {viewAsNonAdmin
                                            ? 'Exit non-admin view'
                                            : 'View as non-admin'}
                                    </span>
                                </button>
                            </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        {!viewAsNonAdmin &&
                            superAdminItems.map((item) => (
                                <SidebarMenuSubItem key={item.href}>
                                    <SidebarMenuSubButton
                                        asChild
                                        isActive={item.href === activeHref}
                                    >
                                        <Link
                                            to={item.href}
                                            onMouseEnter={() => prefetchRoute(item.href)}
                                        >
                                            <item.icon />
                                            <span>{item.title}</span>
                                        </Link>
                                    </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                            ))}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
}

export function NavMenuItem({
    item,
    isActive,
    badge,
}: {
    item: NavItem;
    isActive: boolean;
    /** Status indicator rendered at the end of the row, e.g. a live-refresh mark. */
    badge?: ReactNode;
}) {
    if (item.disabled) {
        return (
            <SidebarMenuItem>
                <SidebarMenuButton
                    tooltip={item.title}
                    disabled
                    size="lg"
                    className="h-9 py-0 text-foreground-subtle group-data-[collapsible=icon]:justify-center [&_svg]:text-foreground-subtle [&_svg]:opacity-70"
                >
                    <item.icon />
                    <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                </SidebarMenuButton>
                <NavSoonPill />
            </SidebarMenuItem>
        );
    }

    return (
        <SidebarMenuItem>
            <SidebarMenuButton
                asChild
                isActive={isActive}
                tooltip={item.title}
                size="lg"
                className="h-9 py-0 group-data-[collapsible=icon]:justify-center"
            >
                <Link to={item.href} onMouseEnter={() => prefetchRoute(item.href)}>
                    <item.icon />
                    <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                </Link>
            </SidebarMenuButton>
            {/* Not a SidebarMenuBadge: that one hides in icon mode, and a status
                indicator has to survive a collapsed sidebar. Collapsed it tucks
                into the icon's top-right corner instead. */}
            {badge ? (
                <span className="pointer-events-none absolute top-1/2 right-2 flex -translate-y-1/2 items-center group-data-[collapsible=icon]:top-0.5 group-data-[collapsible=icon]:right-0.5 group-data-[collapsible=icon]:translate-y-0">
                    {badge}
                </span>
            ) : item.live ? (
                <NavLiveDot />
            ) : null}
        </SidebarMenuItem>
    );
}

/** Quick-access pinned pages, shown at the top of the sidebar. Renders nothing
 *  when the user has no pins. Supports drag-reorder, inline rename, and unpin. */
export function PinnedSection() {
    const location = useLocation();
    const { pins, unpin, rename, reorder } = usePinnedPages();
    const [editingPath, setEditingPath] = useState<string | null>(null);

    // Distance constraint so a click still navigates / a double-click still edits.
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    if (pins.length === 0) return null;

    const currentPath = location.pathname + location.search;

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = pins.findIndex((p) => p.path === active.id);
        const newIndex = pins.findIndex((p) => p.path === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        reorder(arrayMove(pins, oldIndex, newIndex).map((p) => p.path));
    };

    return (
        <SidebarGroup className="px-2 py-2">
            <SidebarGroupLabel className="mb-1.5 px-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-foreground-subtle">
                Pinned
            </SidebarGroupLabel>
            <SidebarGroupContent>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={pins.map((p) => p.path)}
                        strategy={verticalListSortingStrategy}
                    >
                        <SidebarMenu className="gap-px">
                            {pins.map((pin) => (
                                <PinnedMenuItem
                                    key={pin.path}
                                    pin={pin}
                                    isActive={pin.path === currentPath}
                                    isEditing={editingPath === pin.path}
                                    onEditStart={() => setEditingPath(pin.path)}
                                    onEditEnd={() => setEditingPath(null)}
                                    onRename={(label) => rename(pin.path, label)}
                                    onUnpin={() => unpin(pin.path)}
                                />
                            ))}
                        </SidebarMenu>
                    </SortableContext>
                </DndContext>
            </SidebarGroupContent>
        </SidebarGroup>
    );
}

function PinnedMenuItem({
    pin,
    isActive,
    isEditing,
    onEditStart,
    onEditEnd,
    onRename,
    onUnpin,
}: {
    pin: PinnedPage;
    isActive: boolean;
    isEditing: boolean;
    onEditStart: () => void;
    onEditEnd: () => void;
    onRename: (label: string) => void;
    onUnpin: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: pin.path,
    });
    const [draft, setDraft] = useState(pin.label);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : undefined,
    };

    const commit = () => {
        const trimmed = draft.trim();
        if (trimmed && trimmed !== pin.label) onRename(trimmed);
        onEditEnd();
    };

    if (isEditing) {
        return (
            <SidebarMenuItem ref={setNodeRef} style={style}>
                <input
                    ref={(el) => el?.focus()}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') {
                            setDraft(pin.label);
                            onEditEnd();
                        }
                    }}
                    className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm"
                />
            </SidebarMenuItem>
        );
    }

    return (
        <SidebarMenuItem ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <SidebarMenuButton
                asChild
                isActive={isActive}
                tooltip={pin.label}
                size="lg"
                className="h-9 py-0 group-data-[collapsible=icon]:justify-center"
            >
                <Link
                    to={pin.path}
                    onMouseEnter={() => prefetchRoute(pin.path)}
                    onDoubleClick={(e) => {
                        e.preventDefault();
                        setDraft(pin.label);
                        onEditStart();
                    }}
                >
                    <Pin />
                    <span className="group-data-[collapsible=icon]:hidden">{pin.label}</span>
                </Link>
            </SidebarMenuButton>
            <SidebarMenuAction
                onClick={onUnpin}
                aria-label="Unpin"
                title="Unpin"
                className="group-data-[collapsible=icon]:hidden"
            >
                <X />
            </SidebarMenuAction>
        </SidebarMenuItem>
    );
}

/** Rose pinwheel icon mark (extracted from RoseLogo SVG) */
function RoseIconMark({ className }: { className?: string }) {
    return (
        <svg
            width={24}
            height={24}
            viewBox="0 0 14 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            aria-hidden="true"
        >
            <g transform="translate(0, 0.4)">
                <path
                    d="M6.61343 0V4.5C6.61343 5.16274 6.07617 5.7 5.41343 5.7H3.01343L6.61343 0Z"
                    fill="currentColor"
                />
                <path
                    d="M7.21323 12L7.21323 7.5C7.21323 6.83726 7.75049 6.3 8.41323 6.3L10.8132 6.3L7.21323 12Z"
                    fill="currentColor"
                />
                <path
                    d="M12.9133 5.7001L8.41333 5.7001C7.75059 5.7001 7.21333 5.16284 7.21333 4.5001L7.21333 2.1001L12.9133 5.7001Z"
                    fill="currentColor"
                />
                <path
                    d="M0.913331 6.2999L5.41333 6.2999C6.07607 6.2999 6.61333 6.83716 6.61333 7.4999L6.61333 9.8999L0.913331 6.2999Z"
                    fill="currentColor"
                />
            </g>
        </svg>
    );
}
